import * as React from "react"

import { cva, type VariantProps } from "class-variance-authority"
import { X } from "lucide-react"

import { cn } from "../../cn"
import { Tooltip } from "../Tooltip/Tooltip"

const badgeVariants = cva(
  [
    "inline-flex items-center gap-1",
    // Same corner radius as buttons, inputs, and dropdowns.
    "rounded-md text-sm font-medium",
    "transition-colors duration-150",
  ],
  {
    variants: {
      variant: {
        default: "bg-surface-1 text-coval-text",
        contrast: "bg-coval-contrast text-coval-text-on-contrast",
        danger: "bg-coval-danger-surface text-coval-danger-on-surface",
        warning: "bg-coval-warning-surface text-coval-warning-on-surface",
        success: "bg-coval-success-surface text-coval-success-on-surface",
        info: "bg-coval-info-surface text-coval-info-on-surface",
        outline: "border border-coval-border bg-transparent text-coval-text",
      },
      size: {
        status: "h-6 px-2",
        // Matches buttons, inputs, and dropdowns so a chip never sits taller
        // than the controls it shares a row with.
        chip: "h-control px-3",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "status",
    },
  },
)

export interface BadgeProps
  extends
    React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  onDismiss?: () => void
  dismissLabel?: string
}

const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  (
    {
      className,
      variant,
      size,
      onDismiss,
      dismissLabel = "Dismiss",
      children,
      ...props
    },
    ref,
  ) => {
    return (
      <span
        className={cn(badgeVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      >
        {children}
        {onDismiss && (
          <Tooltip content={dismissLabel}>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onDismiss()
              }}
              aria-label={dismissLabel}
              className={cn(
                "ml-0.5 inline-flex shrink-0 items-center justify-center rounded-full",
                "size-3.5",
                "hover:opacity-70",
              )}
            >
              <X className="size-3" />
            </button>
          </Tooltip>
        )}
      </span>
    )
  },
)

Badge.displayName = "Badge"

export { Badge, badgeVariants }
